import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workoutService } from '../services/trackingService';
import { queryKeys } from '../lib/queryClient';

const PAGE_SIZE = 10;

/**
 * Fetch paginated workout logs
 */
const fetchWorkoutLogs = async ({ userId, page, filters }) => {
  if (!userId) return { data: [], totalCount: 0, hasNextPage: false };

  const offset = (page - 1) * PAGE_SIZE;

  // Build date range from filters
  let startDate = null;
  let endDate = null;

  if (filters?.dateRange) {
    const today = new Date();
    if (filters?.dateRange === 'today') {
      startDate = today?.toISOString()?.split('T')?.[0];
      endDate = startDate;
    } else if (filters?.dateRange === 'week') {
      const weekAgo = new Date(today);
      weekAgo?.setDate(today?.getDate() - 7);
      startDate = weekAgo?.toISOString()?.split('T')?.[0];
    } else if (filters?.dateRange === 'month') {
      const monthStart = new Date(today?.getFullYear(), today?.getMonth(), 1);
      startDate = monthStart?.toISOString()?.split('T')?.[0];
    }
  }

  const allWorkouts = await workoutService?.getWorkouts(userId, startDate, endDate);

  // Apply client-side filters (type, search, calorie range)
  let filtered = allWorkouts || [];

  if (filters?.activityType) {
    filtered = filtered?.filter((w) => w?.type === filters?.activityType);
  }
  if (filters?.searchQuery) {
    const q = filters?.searchQuery?.toLowerCase();
    filtered = filtered?.filter(
      (w) =>
        w?.name?.toLowerCase()?.includes(q) ||
        w?.type?.toLowerCase()?.includes(q) ||
        w?.notes?.toLowerCase()?.includes(q)
    );
  }
  if (filters?.calorieRange) {
    filtered = filtered?.filter(
      (w) =>
        w?.calories >= filters?.calorieRange?.[0] &&
        w?.calories <= filters?.calorieRange?.[1]
    );
  }

  const totalCount = filtered?.length;
  const paginated = filtered?.slice(offset, offset + PAGE_SIZE);
  const hasNextPage = offset + PAGE_SIZE < totalCount;

  return { data: paginated, totalCount, hasNextPage, allData: filtered };
};

/**
 * React Query hook for paginated workout logs with caching
 */
export const useWorkoutLogsQuery = (userId, page = 1, filters = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys?.workoutLogs(userId, page, filters),
    queryFn: () => fetchWorkoutLogs({ userId, page, filters }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev) => prev, // keep previous page data while loading next
  });

  const invalidateAll = () =>
    queryClient?.invalidateQueries({ queryKey: queryKeys?.workoutLogsAll(userId) });

  return {
    activities: query?.data?.data || [],
    allActivities: query?.data?.allData || [],
    totalCount: query?.data?.totalCount || 0,
    hasNextPage: query?.data?.hasNextPage || false,
    loading: query?.isLoading,
    isFetching: query?.isFetching,
    error: query?.error?.message || null,
    refetch: invalidateAll,
    pageSize: PAGE_SIZE,
  };
};
